import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

interface TeamsAppInstallResult {
  installed: boolean;
  alreadyInstalled: boolean;
}

export interface TeamsAppLogic {
  /** Status query still in flight — render a placeholder, not the button. */
  isLoading: boolean;
  /** The user can receive platform Teams messages right now. */
  installed: boolean;
  /** The last action here was a fresh install (wording only). */
  freshInstall: boolean;
  isWorking: boolean;
  error: Error | null;
  install: () => Promise<void>;
  remove: () => Promise<void>;
}

/**
 * Status + install/remove for the SC Assistant Teams app on one user.
 * `installed` is server truth (our conversation-ref ledger) overlaid with the
 * most recent local action, so a fresh install hides the button immediately —
 * the ref lands via the bot's install event a few seconds later.
 */
export const useTeamsAppLogic = (tenantId: string | undefined, userId: string): TeamsAppLogic => {
  const queryClient = useQueryClient();
  const statusKey = queryKeys.teamsAppStatus(tenantId ?? '', userId);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: statusKey });

  const status = useQuery({
    queryKey: statusKey,
    enabled: Boolean(tenantId),
    queryFn: () => apiRequest<{ dmReady: boolean }>(`/api/users/${userId}/teams-app`),
  });

  const installMutation = useMutation<TeamsAppInstallResult, Error>({
    mutationFn: () =>
      apiRequest<TeamsAppInstallResult>(`/api/users/${userId}/teams-app`, {
        method: 'POST',
      }),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation<{ removed: boolean }, Error>({
    mutationFn: () =>
      apiRequest<{ removed: boolean }>(`/api/users/${userId}/teams-app`, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });

  // Most recent successful local action wins over (possibly stale) server
  // status; with no local action, server status decides.
  const installedAt = installMutation.isSuccess ? installMutation.submittedAt : 0;
  const removedAt = removeMutation.isSuccess ? removeMutation.submittedAt : 0;
  const overlay = installedAt || removedAt ? installedAt > removedAt : undefined;
  const installed = overlay ?? status.data?.dmReady ?? false;

  return {
    isLoading: status.isLoading,
    installed,
    freshInstall: installMutation.data ? !installMutation.data.alreadyInstalled : false,
    isWorking: installMutation.isPending || removeMutation.isPending,
    error: installMutation.error ?? removeMutation.error,
    install: async () => {
      await installMutation.mutateAsync().catch(() => undefined);
    },
    remove: async () => {
      await removeMutation.mutateAsync().catch(() => undefined);
    },
  };
};
